import { useEffect, useRef, useCallback, type ReactNode } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { Loader2 } from "lucide-react";
import type { PaginatedResponse } from "@/types/common";

export interface VirtualScrollerProps<TItem> {
  queryKey: unknown[];
  queryFn: (page: number, pageSize: number) => Promise<PaginatedResponse<TItem>>;
  renderItem: (item: TItem, index: number) => ReactNode;
  pageSize?: number;
  enabled?: boolean;
  staleTime?: number;
  className?: string;
  emptyMessage?: string;
  loadingRows?: number;
  renderSkeleton?: () => ReactNode;
}

export function VirtualScroller<TItem>({
  queryKey,
  queryFn,
  renderItem,
  pageSize = 20,
  enabled = true,
  staleTime,
  className,
  emptyMessage,
  loadingRows = 5,
  renderSkeleton,
}: VirtualScrollerProps<TItem>) {
  const { t } = useLingui();
  const sentinelRef = useRef<HTMLDivElement>(null);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: [...queryKey, pageSize],
    queryFn: ({ pageParam }) => queryFn(pageParam as number, pageSize),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.meta.hasNext ? last.meta.page + 1 : undefined,
    enabled,
    ...(staleTime !== undefined && { staleTime }),
  });

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: "100px",
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleIntersection]);

  const allItems = data?.pages.flatMap((p) => p.data) ?? [];

  if (isLoading) {
    return (
      <div className={className}>
        {renderSkeleton
          ? Array.from({ length: loadingRows }).map((_, i) => (
              <div key={i}>{renderSkeleton()}</div>
            ))
          : Array.from({ length: loadingRows }).map((_, i) => (
              <div
                key={i}
                className="h-16 rounded-lg bg-(--color-surface-muted) animate-pulse"
              />
            ))}
      </div>
    );
  }

  if (!isLoading && allItems.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-(--color-text-secondary)">{emptyMessage ?? t`No items found`}</p>
    );
  }

  return (
    <div className={className}>
      {allItems.map((item, i) => renderItem(item, i))}

      {/* Sentinel — triggers next page load when visible */}
      <div ref={sentinelRef} className="h-1" />

      {isFetchingNextPage && (
        <div className="flex justify-center py-3">
          <Loader2 className="size-4 animate-spin text-(--color-text-secondary)" />
        </div>
      )}
    </div>
  );
}
